// src/components/InvoiceDemoPage.jsx
// @ts-nocheck
import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const MAX_SCANS = 3;

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.4, ease: "easeOut" },
  }),
};

function ConfidenceBar({ value }) {
  const pct = Math.round(value * 100);
  const color = pct >= 85 ? "#4ade80" : "#fbbf24";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <div style={{ flex: 1, height: 2, background: "rgba(255,255,255,.06)", borderRadius: 1, overflow: "hidden" }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          style={{ height: "100%", background: color, borderRadius: 1 }}
        />
      </div>
      <span style={{ fontSize: 10, color: "#334155", fontFamily: "monospace", minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

function ProcessStrip() {
  const steps = [
    { icon: "📥", label: "Λήψη" },
    { icon: "🔍", label: "Ανάλυση" },
    { icon: "🧠", label: "Εξαγωγή" },
    { icon: "✅", label: "Δεδομένα" },
    { icon: "🔗", label: "ERP-ready" },
  ];
  return (
    <motion.div variants={fadeUp} custom={2}
      style={{ background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: "#334155", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 12 }}>Διαδικασία ανάλυσης</div>
      <div style={{ display: "flex", alignItems: "center" }}>
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div style={{ flex: 1, textAlign: "center" }}>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(74,222,128,.1)", border: "1px solid rgba(74,222,128,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, margin: "0 auto 4px" }}>{s.icon}</div>
              <div style={{ fontSize: 9, color: "#334155" }}>{s.label}</div>
            </div>
            {i < steps.length - 1 && <div style={{ width: 20, height: 1, background: "rgba(74,222,128,.2)", flexShrink: 0, marginBottom: 12 }} />}
          </React.Fragment>
        ))}
      </div>
    </motion.div>
  );
}

function ResultCard({ inv }) {
  const conf = Math.round((inv.confidence || 0.9) * 100);
  const fmt = (v, d = 2) => v === null || v === undefined ? "—" : Number(v).toFixed(d);

  return (
    <motion.div initial="hidden" animate="show" variants={{ show: { transition: { staggerChildren: 0.1 } } }}>
      {/* Header card */}
      <motion.div variants={fadeUp} custom={0}
        style={{ background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,.04)", background: "rgba(255,255,255,.015)" }}>
          <span style={{ fontSize: 13 }}>🏢</span>
          <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: ".07em" }}>Στοιχεία τιμολογίου</span>
          <span style={{ marginLeft: "auto", fontSize: 10, background: conf >= 85 ? "rgba(74,222,128,.1)" : "rgba(251,191,36,.1)", color: conf >= 85 ? "#4ade80" : "#fbbf24", border: `1px solid ${conf >= 85 ? "rgba(74,222,128,.15)" : "rgba(251,191,36,.15)"}`, borderRadius: 20, padding: "2px 8px", fontWeight: 500 }}>{conf}% ακρίβεια</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: 14 }}>
          <div style={{ gridColumn: "span 2" }}>
            <div style={{ fontSize: 10, color: "#334155", marginBottom: 2 }}>Προμηθευτής</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: "#a78bfa" }}>{inv.vendor || "—"}</div>
            <ConfidenceBar value={inv.confidence || 0.9} />
          </div>
          {[
            { l: "Αρ. τιμολογίου", v: inv.invoice_number, mono: true },
            { l: "Ημερομηνία", v: inv.date },
            { l: "Λήξη πληρωμής", v: inv.due_date },
            { l: "Τρόπος πληρωμής", v: inv.payment_method },
          ].map((f, i) => (
            <div key={i}>
              <div style={{ fontSize: 10, color: "#334155", marginBottom: 2 }}>{f.l}</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: f.mono ? "#93c5fd" : "#f1f5f9", fontFamily: f.mono ? "monospace" : "inherit" }}>{f.v || "—"}</div>
            </div>
          ))}
          {inv.notes && (
            <div style={{ gridColumn: "span 2" }}>
              <div style={{ fontSize: 10, color: "#334155", marginBottom: 2 }}>Παρατηρήσεις</div>
              <div style={{ fontSize: 11, color: "#475569" }}>{inv.notes}</div>
            </div>
          )}
        </div>
      </motion.div>

      {/* Lines card */}
      {inv.lines && inv.lines.length > 0 && (
        <motion.div variants={fadeUp} custom={1}
          style={{ background: "rgba(255,255,255,.025)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 12, overflow: "hidden", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,.04)", background: "rgba(255,255,255,.015)" }}>
            <span style={{ fontSize: 13 }}>📦</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: "#64748b", textTransform: "uppercase", letterSpacing: ".07em" }}>Γραμμές προϊόντων</span>
            <span style={{ marginLeft: "auto", fontSize: 10, background: "rgba(74,222,128,.1)", color: "#4ade80", border: "1px solid rgba(74,222,128,.15)", borderRadius: 20, padding: "2px 8px" }}>{inv.lines.length} items</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Περιγραφή", "Ποσ.", "Τιμή", "Σύνολο"].map((h, i) => (
                  <th key={i} style={{ fontSize: 10, color: "#334155", textTransform: "uppercase", letterSpacing: ".06em", padding: "7px 14px", textAlign: i > 0 ? "right" : "left", borderBottom: "1px solid rgba(255,255,255,.04)", fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i}>
                  <td style={{ padding: "8px 14px", fontSize: 12, color: "#cbd5e1", borderBottom: "1px solid rgba(255,255,255,.025)" }}>{l.description || "—"}</td>
                  <td style={{ padding: "8px 14px", fontSize: 12, color: "#a78bfa", fontFamily: "monospace", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,.025)" }}>{l.quantity ?? "—"} {l.unit || ""}</td>
                  <td style={{ padding: "8px 14px", fontSize: 12, color: "#93c5fd", fontFamily: "monospace", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,.025)" }}>€{fmt(l.unit_price)}</td>
                  <td style={{ padding: "8px 14px", fontSize: 12, color: "#93c5fd", fontFamily: "monospace", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,.025)" }}>€{fmt(l.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,.07)" }}>
            <span style={{ fontSize: 11, color: "#334155" }}>Καθαρή αξία</span>
            <span style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 500, color: "#60a5fa" }}>€{fmt(inv.subtotal)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 14px 10px" }}>
            <span style={{ fontSize: 11, color: "#334155" }}>ΦΠΑ {inv.vat_rate || 24}%</span>
            <span style={{ fontFamily: "monospace", fontSize: 13, color: "#64748b" }}>€{fmt(inv.vat_amount)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderTop: "1px solid rgba(255,255,255,.08)", background: "rgba(96,165,250,.05)" }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: "#cbd5e1" }}>Σύνολο</span>
            <span style={{ fontFamily: "monospace", fontSize: 17, fontWeight: 500, color: "#60a5fa" }}>€{fmt(inv.total)}</span>
          </div>
        </motion.div>
      )}

      <ProcessStrip />
    </motion.div>
  );
}

// ─── Gate Screen ────────────────────────────────────────────────────────────
function GateScreen({ onUnlock }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const check = async () => {
    setLoading(true);
    setError("");
    // Quick pre-check (real validation is server-side per scan)
    if (!pw.trim()) { setError("Εισάγετε κωδικό."); setLoading(false); return; }
    onUnlock(pw.trim());
    setLoading(false);
  };

  return (
    <div style={{ minHeight: 600, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(99,102,241,.1)", border: "1px solid rgba(99,102,241,.2)", borderRadius: 20, padding: "5px 14px", fontSize: 11, color: "#818cf8", marginBottom: 24 }}>
          🔒 Περιορισμένη Πρόσβαση Demo
        </div>
        <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 700, color: "#f8fafc", lineHeight: 1.2, marginBottom: 10 }}>
          Invoice Intelligence<br /><span style={{ color: "#6366f1" }}>για ERP ενσωμάτωση</span>
        </h1>
        <p style={{ fontSize: 14, color: "#475569", marginBottom: 32, maxWidth: 360, lineHeight: 1.7 }}>
          Δείτε πώς η AI ανάλυση τιμολογίων μπορεί να ενσωματωθεί στο σύστημά σας — αυτόματα.
        </p>

        {/* Live Demo Video */}
        <div style={{ width: "100%", maxWidth: 640, marginBottom: 32, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,.08)", background: "rgba(0,0,0,0.4)", boxShadow: "0 10px 40px -10px rgba(0,0,0,0.5)" }}>
          <video 
            src="/assets/invoice_demo.mp4" 
            controls 
            playsInline
            style={{ width: "100%", display: "block", objectFit: "cover" }} 
          />
        </div>

        <div style={{ width: "100%", maxWidth: 320, display: "flex", flexDirection: "column", gap: 12 }}>
          <input
            type="password" value={pw} placeholder="Κωδικός πρόσβασης"
            onChange={e => setPw(e.target.value)}
            onKeyDown={e => e.key === "Enter" && check()}
            style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 10, padding: "13px 16px", color: "#f1f5f9", fontSize: 14, outline: "none", width: "100%" }}
          />
          {error && <div style={{ fontSize: 12, color: "#f87171", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.15)", borderRadius: 8, padding: "8px 12px" }}>{error}</div>}
          <button onClick={check} disabled={loading}
            style={{ background: "linear-gradient(135deg,#3b82f6,#6366f1)", border: "none", borderRadius: 10, padding: 14, color: "white", fontSize: 14, fontWeight: 500, cursor: "pointer", opacity: loading ? .5 : 1 }}>
            {loading ? "..." : "Είσοδος στο Demo →"}
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 24, width: "100%", maxWidth: 320 }}>
          {[["📄","Υποστήριξη","PDF · JPG · PNG"],["⚡","Ταχύτητα","< 5 δευτερόλεπτα"],["🔗","Output","JSON · ERP-ready"],["🇬🇷","Γλώσσα","Ελληνικά · Αγγλικά"]].map(([ic,lb,vl],i) => (
            <div key={i} style={{ background: "rgba(255,255,255,.02)", border: "1px solid rgba(255,255,255,.05)", borderRadius: 8, padding: "10px 12px", textAlign: "left" }}>
              <div style={{ fontSize: 14, marginBottom: 4 }}>{ic}</div>
              <div style={{ fontSize: 11, color: "#475569" }}>{lb}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500, marginTop: 2 }}>{vl}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Limit Screen ────────────────────────────────────────────────────────────
function LimitScreen() {
  return (
    <div style={{ minHeight: 600, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", textAlign: "center" }}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: "#f8fafc", marginBottom: 8 }}>Ολοκλήρωσες το demo</h2>
        <p style={{ fontSize: 14, color: "#475569", marginBottom: 28, maxWidth: 320, lineHeight: 1.7 }}>
          Είδες τις δυνατότητες του Invoice Intelligence. Για πλήρη ενσωμάτωση στο σύστημά σας, ας μιλήσουμε.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <a href="mailto:dellios@orionintelligence.net?subject=Invoice Intelligence - Ενδιαφέρον Ενσωμάτωσης"
            style={{ background: "linear-gradient(135deg,#3b82f6,#6366f1)", borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 500, color: "white", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
            ✉ Στείλε email
          </a>
          <a href="https://calendly.com/dellios-orionintelligence" target="_blank" rel="noreferrer"
            style={{ background: "rgba(255,255,255,.05)", borderRadius: 10, padding: "12px 20px", fontSize: 13, fontWeight: 500, color: "#94a3b8", textDecoration: "none", border: "1px solid rgba(255,255,255,.08)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            📅 Κλείσε κλήση
          </a>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Demo UI ─────────────────────────────────────────────────────────────
function DemoUI({ password }) {
  const [scansLeft, setScansLeft] = useState(MAX_SCANS);
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [filename, setFilename] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | loading | result | limit
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loadStep, setLoadStep] = useState("Αναγνώριση δομής εγγράφου");
  const stepRef = useRef(null);

  const LD_STEPS = ["Αναγνώριση δομής εγγράφου","Εντοπισμός στοιχείων vendor","Εξαγωγή γραμμών προϊόντων","Υπολογισμός ποσών & ΦΠΑ","Επαλήθευση δεδομένων"];

  const handleFile = (file) => {
    if (!file) return;
    setIsPdf(file.type === "application/pdf");
    setFileType(file.type);
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      setFileData(e.target.result.split(",")[1]);
      setPreviewSrc(file.type.startsWith("image/") ? e.target.result : null);
    };
    reader.readAsDataURL(file);
  };

  const doScan = async () => {
    if (!fileData || scansLeft <= 0) return;
    const newLeft = scansLeft - 1;
    setScansLeft(newLeft);
    setPhase("loading");
    setError("");
    let idx = 0;
    stepRef.current = setInterval(() => {
      idx = (idx + 1) % LD_STEPS.length;
      setLoadStep(LD_STEPS[idx]);
    }, 1300);

    try {
      const res = await fetch("/api/scan-invoice-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageData: fileData, mediaType: fileType, isPdf, demoPassword: password }),
      });
      const data = await res.json();
      clearInterval(stepRef.current);
      if (!res.ok) throw new Error(data.error || "Σφάλμα server");
      setResult(data);
      setPhase(newLeft === 0 ? "limit-after" : "result");
    } catch (err) {
      clearInterval(stepRef.current);
      setError(err.message);
      setPhase("result");
    }
  };

  if (phase === "limit") return <LimitScreen />;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#3b82f6,#6366f1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white", fontFamily: "Syne, sans-serif" }}>OI</div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", fontFamily: "Syne, sans-serif" }}>Invoice Intelligence</div>
          <div style={{ fontSize: 11, color: "#334155" }}>Orion Intelligence — Confidential Demo</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", gap: 4 }}>
            {Array.from({ length: MAX_SCANS }).map((_, i) => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: i < (MAX_SCANS - scansLeft) ? (scansLeft === 1 && i === MAX_SCANS - 1 ? "#f59e0b" : "#6366f1") : "rgba(255,255,255,.08)", transition: "background .3s" }} />
            ))}
          </div>
          <span style={{ fontSize: 11, color: "#334155" }}>{scansLeft} scans απομένουν</span>
        </div>
      </div>

      {/* Body */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left */}
        <div style={{ width: "40%", borderRight: "1px solid rgba(255,255,255,.06)", padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: "#334155", letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Φόρτωση τιμολογίου</div>
            <label style={{ display: "block", border: "1.5px dashed rgba(99,102,241,.25)", borderRadius: 12, padding: "24px 16px", textAlign: "center", cursor: "pointer", background: "rgba(99,102,241,.02)" }}>
              <input type="file" accept="image/*,.pdf" style={{ display: "none" }} onChange={e => handleFile(e.target.files[0])} />
              <div style={{ fontSize: 22, marginBottom: 8 }}>📄</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#cbd5e1", marginBottom: 3 }}>Επίλεξε αρχείο</div>
              <div style={{ fontSize: 11, color: "#334155" }}>Τιμολόγιο αγοράς / πώλησης</div>
              <div style={{ display: "flex", gap: 5, justifyContent: "center", marginTop: 8 }}>
                {["PDF","JPG","PNG"].map(f => <span key={f} style={{ background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.07)", borderRadius: 4, padding: "2px 7px", fontSize: 10, color: "#475569" }}>{f}</span>)}
              </div>
            </label>
          </div>
          <div>
            <div style={{ fontSize: 10, color: "#334155", letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 500, marginBottom: 8 }}>Προεπισκόπηση</div>
            <div style={{ border: "1px solid rgba(255,255,255,.06)", borderRadius: 12, background: "rgba(255,255,255,.01)", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 140, overflow: "hidden", position: "relative" }}>
              {previewSrc ? <img src={previewSrc} alt="" style={{ width: "100%", objectFit: "contain" }} /> :
                fileData ? <div style={{ textAlign: "center", padding: 16 }}><div style={{ fontSize: 28, marginBottom: 6 }}>📋</div><div style={{ fontSize: 11, color: "#475569" }}>{filename}</div></div> :
                <div style={{ fontSize: 12, color: "#1e293b" }}>Δεν έχει επιλεγεί</div>}
              {filename && <div style={{ position: "absolute", bottom: 6, left: 6, right: 6, background: "rgba(0,0,0,.75)", borderRadius: 6, padding: "3px 8px", fontSize: 10, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{filename}</div>}
            </div>
          </div>
          {scansLeft === 1 && <div style={{ background: "rgba(245,158,11,.06)", border: "1px solid rgba(245,158,11,.15)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#fbbf24", textAlign: "center" }}>⚠ Τελευταίο διαθέσιμο scan</div>}
          <button onClick={doScan} disabled={!fileData || phase === "loading" || scansLeft === 0}
            style={{ background: "linear-gradient(135deg,#3b82f6,#6366f1)", border: "none", borderRadius: 10, padding: 13, color: "white", fontSize: 14, fontWeight: 500, cursor: "pointer", opacity: (!fileData || phase === "loading" || scansLeft === 0) ? .35 : 1, transition: "opacity .2s" }}>
            {phase === "loading" ? "Ανάλυση..." : "Ανάλυση τιμολογίου"}
          </button>
        </div>

        {/* Right */}
        <div style={{ flex: 1, padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          <AnimatePresence mode="wait">
            {phase === "idle" && (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#1e293b", textAlign: "center", padding: 30 }}>
                <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth=".6" viewBox="0 0 24 24" style={{ opacity: .1 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <p style={{ fontSize: 12, color: "#1e293b" }}>Φόρτωσε τιμολόγιο<br/>για να ξεκινήσει η ανάλυση</p>
              </motion.div>
            )}
            {phase === "loading" && (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 30 }}>
                <div style={{ width: 32, height: 32, border: "2px solid rgba(99,102,241,.15)", borderTopColor: "#6366f1", borderRadius: "50%", animation: "spin .8s linear infinite" }} />
                <div style={{ fontSize: 13, color: "#475569" }}>Επεξεργασία εγγράφου…</div>
                <div style={{ fontSize: 11, color: "#334155", fontFamily: "monospace" }}>{loadStep}</div>
              </motion.div>
            )}
            {(phase === "result" || phase === "limit-after") && (
              <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {error && <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.15)", borderRadius: 10, padding: "14px", fontSize: 13, color: "#f87171" }}>Σφάλμα: {error}</div>}
                {result && <ResultCard inv={result} />}
                {/* CTA */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                  style={{ background: "rgba(99,102,241,.05)", border: "1px solid rgba(99,102,241,.12)", borderRadius: 12, padding: 18 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 9, background: "rgba(99,102,241,.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>⚡</div>
                    <div>
                      <div style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 600, color: "#a5b4fc", marginBottom: 3 }}>Έτοιμο για ενσωμάτωση</div>
                      <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.7 }}>Αυτά τα δεδομένα μπορούν να τροφοδοτήσουν αυτόματα αποθήκη, λογιστική και κόστη στο ERP σας — χωρίς χειροκίνητη καταχώρηση.</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <a href="https://calendly.com/dellios-orionintelligence" target="_blank" rel="noreferrer"
                      style={{ background: "rgba(99,102,241,.15)", color: "#a5b4fc", border: "1px solid rgba(99,102,241,.25)", borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      📅 Κλείσε κλήση
                    </a>
                    <a href="mailto:dellios@orionintelligence.net?subject=Invoice Intelligence - Ενδιαφέρον"
                      style={{ background: "rgba(255,255,255,.04)", color: "#64748b", border: "1px solid rgba(255,255,255,.07)", borderRadius: 8, padding: "9px 16px", fontSize: 12, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                      ✉ Εκδήλωσε ενδιαφέρον
                    </a>
                  </div>
                </motion.div>
                {phase === "limit-after" && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }}>
                    <LimitScreen />
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── Root Export ──────────────────────────────────────────────────────────────
export default function InvoiceDemoPage() {
  const [password, setPassword] = useState(null);

  return (
    <div style={{ background: "#07090f", minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700&display=swap" rel="stylesheet" />
      <div style={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)", borderRadius: 10, padding: "8px 14px", color: "#94a3b8", fontSize: 13, textDecoration: "none", transition: "all .2s" }}>
          <span style={{ fontSize: 16 }}>←</span> Επιστροφή
        </a>
      </div>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: "linear-gradient(90deg,transparent,#3b82f6 30%,#6366f1 60%,transparent)" }} />
      <div style={{ maxWidth: 900, margin: "0 auto", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        {!password ? <GateScreen onUnlock={setPassword} /> : <DemoUI password={password} />}
      </div>
    </div>
  );
}
